import {
  SprayCan,
  ClipboardList,
  Trash2,
  Droplets,
  MinusSquare,
  AlertTriangle,
  PanelTop,
  Leaf,
  PackageOpen,
  Boxes,
  Snowflake,
  Sparkles,
  MapPin,
  FileText,
} from "lucide-react";

const SERVICES_IMG =
  "https://customer-assets.emergentagent.com/job_page-generator-46/artifacts/p5mallie_Xteriorworks2.png";

const CATEGORIES = [
  {
    label: "Cart & Asset Services",
    code: "A",
    items: [
      {
        icon: SprayCan,
        name: "Cart Sanitation",
        desc: "Full pressure wash and EPA-registered disinfectant treatment on every cart, every visit. ATP bacteria testing before and after, documented for your team.",
        tag: "Core",
      },
      {
        icon: ClipboardList,
        name: "Asset Condition Reporting",
        desc: "Every cart tagged, tracked, and logged. After each service you receive a detailed condition report documenting damage, wear, and action items.",
        tag: "Core",
      },
      {
        icon: Trash2,
        name: "Curb & Corral Cleaning",
        desc: "Cart corrals, return stations, and curb areas pressure washed and sanitized on every visit. Clean corrals signal a well-managed property.",
        tag: "Core",
      },
    ],
  },
  {
    label: "Parking Lot Maintenance",
    code: "B",
    items: [
      {
        icon: Droplets,
        name: "Lot Pressure Washing",
        desc: "Commercial-grade pressure washing of lot surfaces, entrances, drive aisles, and storefronts. Removes oil, grime, and seasonal debris.",
        tag: "Lot",
      },
      {
        icon: MinusSquare,
        name: "Line Striping & Repainting",
        desc: "Parking stall lines, fire lanes, ADA markings, and directional striping repainted to code. Fresh striping reduces liability.",
        tag: "Lot",
      },
      {
        icon: AlertTriangle,
        name: "Pothole & Crack Reporting",
        desc: "Surface hazards photographed and logged in your condition report. Catch liability exposures before they become incidents.",
        tag: "Lot",
      },
    ],
  },
  {
    label: "Grounds & Appearance",
    code: "C",
    items: [
      {
        icon: PanelTop,
        name: "Signage Cleaning & Maintenance",
        desc: "Exterior signage, wayfinding markers, and entrance signs cleaned and inspected on schedule. Damaged signage flagged for your facilities team.",
        tag: "Grounds",
      },
      {
        icon: Leaf,
        name: "Landscaping & Grounds Upkeep",
        desc: "Scheduled grounds maintenance including edging, bed cleanup, and perimeter upkeep. Keeps your property perimeter as maintained as your interior.",
        tag: "Grounds",
      },
      {
        icon: PackageOpen,
        name: "Trash, Litter & Debris Removal",
        desc: "Lot litter pickup, debris clearing, and loose trash removal from parking areas, lot perimeters, and exterior zones.",
        tag: "Grounds",
      },
    ],
  },
  {
    label: "Specialized & Seasonal",
    code: "D",
    items: [
      {
        icon: Boxes,
        name: "Dumpster Area Cleaning",
        desc: "Pressure washing and deodorizing of dumpster pads, enclosures, and surrounding surfaces — one of the most noticed areas on any property.",
        tag: "Specialized",
      },
      {
        icon: Snowflake,
        name: "Snow & Ice Removal",
        desc: "Seasonal lot clearing, salting, and ice management for parking areas, entrances, and walkways. Keeps your property safe year-round.",
        tag: "Seasonal",
      },
      {
        icon: Sparkles,
        name: "Seasonal Decorations",
        desc: "Installation, maintenance, and removal of exterior seasonal décor — holiday lighting, banners, wreaths. Setup and takedown every season.",
        tag: "Seasonal",
      },
    ],
  },
  {
    label: "Enterprise",
    code: "E",
    items: [
      {
        icon: MapPin,
        name: "Multi-Location Programs",
        desc: "Operating a regional chain or multiple properties? One unified route and reporting structure across all sites. One contract, one contact.",
        tag: "Enterprise",
      },
      {
        icon: FileText,
        name: "Repair Referral Network",
        desc: "When we identify a cart or asset requiring repair, we document it and connect you with vetted vendors — with no gap in your service record.",
        tag: "Coming Soon",
      },
    ],
  },
];

function ServiceCard({ icon: Icon, name, desc, tag, featured }) {
  return (
    <div
      className={`group relative border border-[var(--xw-line)] p-5 sm:p-6 md:p-7 transition-colors duration-200 ${
        featured
          ? "bg-[var(--xw-ink)] text-white hover:bg-[var(--xw-orange)]"
          : "bg-white hover:bg-[var(--xw-ink)] hover:text-white"
      }`}
      data-testid={`service-card-${name.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="flex items-start justify-between">
        <div
          className={`w-10 h-10 flex items-center justify-center border ${
            featured
              ? "border-white/30"
              : "border-[var(--xw-line)] group-hover:border-white/30"
          }`}
        >
          <Icon size={18} strokeWidth={1.5} />
        </div>
        <span
          className={`font-mono-xw text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${
            featured
              ? "border-white/40 text-white"
              : "border-[var(--xw-line)] text-[var(--xw-muted)] group-hover:border-white/40 group-hover:text-white"
          }`}
        >
          {tag}
        </span>
      </div>
      <h3 className="mt-5 md:mt-6 font-display font-bold tracking-tight text-xl sm:text-2xl md:text-[26px] leading-tight">
        {name}
      </h3>
      <p
        className={`mt-3 text-[14px] leading-relaxed ${
          featured ? "text-white/80" : "text-[var(--xw-ink-soft)] group-hover:text-white/80"
        }`}
      >
        {desc}
      </p>
    </div>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-16 md:py-32 bg-[var(--xw-bg)] border-b border-[var(--xw-line)]"
    >
      <div className="xw-container">
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono-xw text-[11px] tracking-[0.22em] uppercase text-[var(--xw-orange)]">
                § 02 — What We Deliver
              </span>
              <span className="h-px w-16 bg-[var(--xw-line)]" />
            </div>
            <h2
              data-testid="services-headline"
              className="font-display font-black uppercase tracking-[-0.03em] leading-[0.9]"
              style={{ fontSize: "clamp(2rem, 7vw, 5rem)" }}
            >
              Every Service <br />
              <span className="italic font-medium" style={{ color: "var(--xw-orange)" }}>
                Your Exterior Needs.
              </span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <p className="text-[16px] md:text-[17px] leading-relaxed text-[var(--xw-ink-soft)]">
              One partner. One contract. Every exterior service your property
              requires — delivered on schedule, documented, and reported back
              to your team.
            </p>
          </div>
        </div>

        {/* Featured hero tile using image */}
        <div className="mt-10 md:mt-14 grid grid-cols-12 gap-0 border border-[var(--xw-line)]">
          <div className="col-span-12 lg:col-span-6 relative min-h-[260px] sm:min-h-[340px] lg:min-h-[420px]">
            <img
              src={SERVICES_IMG}
              alt="Xterior Works crew pressure-washing a dumpster enclosure pad"
              className="absolute inset-0 w-full h-full object-cover"
              draggable="false"
            />
            <div className="absolute top-3 left-3 bg-[var(--xw-ink)] text-white px-2.5 py-1 font-mono-xw text-[10px] tracking-[0.18em]">
              FIELD / 14
            </div>
          </div>
          <div className="col-span-12 lg:col-span-6 p-6 sm:p-8 md:p-12 bg-[var(--xw-ink)] text-white flex flex-col justify-between">
            <div>
              <span className="font-mono-xw text-[10px] tracking-[0.22em] uppercase text-[var(--xw-orange)]">
                Featured / Most Neglected, Most Noticed
              </span>
              <h3
                className="mt-3 md:mt-4 font-display font-bold uppercase tracking-tight leading-[0.95]"
                style={{ fontSize: "clamp(1.75rem, 5.5vw, 3rem)" }}
              >
                Dumpster Pads, <br />
                Done Right.
              </h3>
              <p className="mt-5 md:mt-6 text-white/75 text-[14px] sm:text-[15px] md:text-[16px] leading-relaxed max-w-[56ch]">
                The single most overlooked surface on your property is also the
                one customers smell and see. We bring commercial pressure
                washing and enzyme deodorizing to every dumpster enclosure on
                your service program.
              </p>
            </div>
            <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-x-4 sm:gap-x-6 gap-y-2 font-mono-xw text-[10px] sm:text-[11px] tracking-[0.18em] sm:tracking-[0.2em] uppercase text-white/60">
              <span>PSI 3,500+</span>
              <span className="hidden sm:inline">·</span>
              <span>Enzyme Deodorizer</span>
              <span className="hidden sm:inline">·</span>
              <span>EPA Compliant</span>
            </div>
          </div>
        </div>

        {/* Categorized service grid */}
        <div className="mt-14 md:mt-20 space-y-12 md:space-y-16">
          {CATEGORIES.map((cat, ci) => (
            <div key={cat.label} data-testid={`service-category-${cat.code}`}>
              <div className="flex items-baseline gap-3 sm:gap-6 mb-4 md:mb-6">
                <span
                  className="font-display font-black text-xl sm:text-2xl md:text-3xl shrink-0"
                  style={{ color: "var(--xw-orange)" }}
                >
                  {cat.code}
                </span>
                <h3 className="font-display font-bold tracking-tight text-base sm:text-xl md:text-2xl uppercase">
                  {cat.label}
                </h3>
                <span className="flex-1 h-px bg-[var(--xw-line-soft)]" />
                <span className="hidden md:inline font-mono-xw text-[10px] tracking-[0.22em] uppercase text-[var(--xw-muted)] whitespace-nowrap">
                  {String(cat.items.length).padStart(2, "0")} services
                </span>
              </div>
              <div
                className={`grid gap-0 ${
                  cat.items.length === 2
                    ? "grid-cols-1 md:grid-cols-2"
                    : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                }`}
              >
                {cat.items.map((s, i) => (
                  <div key={s.name} className="-ml-px -mt-px xw-reveal">
                    <ServiceCard
                      {...s}
                      featured={ci === 0 && i === 0}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
